import { useMemo } from "react";

import type { NautilusDesignerAppState } from "../../state/shared.ts";
import type { ConditionBuildable } from "../../types/index.ts";
import { resolveValueExpressionContextSelector } from "../../values/index.ts";
import { groupConditionBuildableCreate } from "./group/index.ts";
import { localeConditionBuildableCreate } from "./locale/index.ts";
import { negationConditionBuildableCreate } from "./negation/index.ts";
import { randomConditionBuildableCreate } from "./random/index.ts";
import { valueExpressionConditionBuildableCreate } from "./value-expression/index.ts";

interface StateSource {
  getState: () => NautilusDesignerAppState;
}

/**
 * The conditions offered on every trigger, and whatever each of them needs to be
 * made. One list, made once: the trigger designer memoises over its identity, so
 * rebuilding it per render would re-derive every buildable behind it.
 *
 * State comes in as `getState` rather than through a selector hook because this is
 * assembled outside the store's own Provider — and because a hook would be the
 * worse choice anyway: it would re-run as node values change, when what a
 * dependency reads is what matters, not when it was read.
 */
export const useUtilityConditionBuildables = ({ getState }: StateSource): ReadonlyArray<ConditionBuildable> =>
  useMemo(
    () => [
      groupConditionBuildableCreate(),
      negationConditionBuildableCreate(),
      randomConditionBuildableCreate(),
      localeConditionBuildableCreate(),
      valueExpressionConditionBuildableCreate((subject) => resolveValueExpressionContextSelector(getState())(subject)),
    ],
    [getState],
  );
